import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, Building2, Calendar, DollarSign, Target, FileText, Clock, StickyNote, Save, Pencil, Check, X, Trophy, Sparkles, AlertCircle, Bot } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MarkLostModal } from '../components/opportunity/MarkLostModal';
import { OpportunityDriveFolderSection } from '../components/opportunity/OpportunityDriveFolderSection';
import { ActivityTimeline, AddActivityModal, CompleteActivityModal } from '../components/activity';
import { CrmNoteFeed } from '../components/notes/CrmNoteFeed';
import { useCreateActivity, useUpdateActivity, useCompleteActivity, useDeleteActivity } from '../hooks';
import { AttachmentsList } from '@/features/attachments/components/AttachmentsList';
import { formatCurrency, cn } from '@/lib/utils';
import { api } from '@/lib/api';
import { mastraClient } from '@/lib/mastra-client';
import { PIPELINES } from '../constants';
import { useMarkOpportunityLost } from '../hooks/useCrmPipeline';
import type { PipelineType, PipelineOpportunity, CrmActivityDetail } from '../types';
import { AgentPanel } from '@/shared/components/AgentPanel';
import { CrmDetailLayout } from '../components/CrmDetailLayout';
import { OpportunityEstimatesTab } from '@/features/estimates/components/OpportunityEstimatesTab';

interface OpportunityDetail {
  id: string;
  accountId?: string;
  companyId?: string;
  tenderId?: string;
  accountName?: string;
  accountNumber?: string;
  companyName?: string;
  companyWebsite?: string;
  companyIndustry?: string;
  companyCity?: string;
  companyCountry?: string;
  title: string;
  type: string;
  stage: string;
  pipeline?: PipelineType;
  description?: string;
  estimatedValue?: number;
  currency?: string;
  probability?: number;
  priority?: string;
  targetDate?: string;
  stageEnteredAt?: string;
  createdAt?: string;
  updatedAt?: string;
  lossReason?: string;
  gdriveFolderId?: string | null;
  signalData?: Record<string, unknown>;
  tender?: {
    id: string;
    source: string;
    name: string;
    url: string;
    solicitationNumber?: string;
    confidenceScore?: number;
    relevanceScore?: number;
    summary?: string;
    organisationName?: string;
    publishDate?: string;
    deadline?: string;
    matchedKeyword?: string;
    description?: string;
  };
  stageHistory?: Array<{
    id: string;
    fromStage: string;
    toStage: string;
    createdAt: string;
    notes?: string;
  }>;
}

// Notes API (uses opportunity description field for now)
async function updateOpportunityNotes(opportunityId: string, notes: string): Promise<void> {
  const res = await fetch(`/api/v4/tenders/${opportunityId}/notes`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ notes }),
  });
  if (!res.ok) throw new Error('Failed to save notes');
}

function InlineField({
  icon, label, display, isEditing, onEdit, onSave, onCancel, input,
}: {
  icon: React.ReactNode;
  label: string;
  display: React.ReactNode;
  isEditing: boolean;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  input: React.ReactNode;
}) {
  return (
    <div className="group">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-0.5">{icon} {label}</div>
      {isEditing ? (
        <div className="flex items-center gap-1">
          {input}
          <button
            className="p-1 rounded text-emerald-600 hover:text-emerald-800"
            title="Save"
            onClick={(e) => { e.preventDefault(); onSave(); }}
          >
            <Check className="h-3.5 w-3.5" />
          </button>
          <button
            className="p-1 rounded text-muted-foreground hover:text-foreground"
            title="Cancel"
            onClick={(e) => { e.preventDefault(); onCancel(); }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className="text-base font-semibold">{display}</span>
          <button
            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-foreground"
            title={`Edit ${label}`}
            onClick={onEdit}
          >
            <Pencil className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

async function fetchOpportunity(id: string): Promise<OpportunityDetail> {
  try {
    return await api.get<OpportunityDetail>(`/api/v4/opportunities/${id}`);
  } catch (err: any) {
    throw new Error(`[GET /api/v4/opportunities/${id}] ${err.status || ''} ${err.message || 'Unknown error'}`, { cause: err });
  }
}

export function OpportunityDetailPage() {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [tab, setTab] = useState('notes');

  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');

  const { data: opp, isLoading, error } = useQuery({
    queryKey: ['opportunity', opportunityId],
    queryFn: () => fetchOpportunity(opportunityId!),
    enabled: !!opportunityId,
  });

  useEffect(() => {
    if (opp) setNotes(opp.description || '');
  }, [opp?.description]);

  const notesMutation = useMutation({
    mutationFn: (text: string) => updateOpportunityNotes(opportunityId!, text),
    onSuccess: () => toast.success('Notes saved'),
    onError: (err) => toast.error('Failed to save notes', { description: err.message }),
  });

  const [briefRunning, setBriefRunning] = useState(false);
  const [briefResult, setBriefResult] = useState<{ briefUrl: string; documentsToPrepare: string[] } | null>(null);
  const [briefError, setBriefError] = useState<string | null>(null);

  async function handleGenerateBrief() {
    setBriefRunning(true);
    setBriefError(null);
    try {
      const run = await mastraClient.getWorkflow('tender-brief-generation').createRun();
      const data = await run.startAsync({ inputData: { opportunityId } });
      const result = (data as { result?: Record<string, unknown> }).result ?? (data as Record<string, unknown>);
      setBriefResult({
        briefUrl: (result.briefUrl as string) ?? '',
        documentsToPrepare: (result.documentsToPrepare as string[]) ?? [],
      });
      toast.success('RFP brief generated', { description: 'Brief saved to Google Drive' });
    } catch (err: any) {
      setBriefError(err.message);
      toast.error('Brief generation failed', { description: err.message });
    } finally {
      setBriefRunning(false);
    }
  }

  // Inline editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [editingField, setEditingField] = useState<'estimatedValue' | 'targetDate' | 'priority' | null>(null);
  const [fieldDraft, setFieldDraft] = useState('');

  const updateOppMutation = useMutation({
    mutationFn: (patch: Partial<{ title: string; estimatedValue: number | null; targetDate: string | null; priority: string }>) =>
      api.patch<OpportunityDetail>(`/api/v4/opportunities/${opportunityId}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] });
      setIsEditingTitle(false);
      setEditingField(null);
      toast.success('Opportunity updated');
    },
    onError: (err: Error) => toast.error('Update failed', { description: err.message }),
  });

  // Terminal-stage confirmation
  const [confirmWonOpen, setConfirmWonOpen] = useState(false);
  const [markLostOpen, setMarkLostOpen] = useState(false);
  const markLostMutation = useMarkOpportunityLost();

  const stageMutation = useMutation({
    mutationFn: (newStage: string) =>
      api.put<OpportunityDetail>(`/api/v4/opportunities/${opportunityId}/stage`, { stage: newStage }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
      queryClient.invalidateQueries({ queryKey: ['crm-pipeline'] });
      toast.success(`Moved to ${data.stage}`);
    },
    onError: (err: Error) => toast.error('Stage change failed', { description: err.message }),
  });

  // Activity feed (notes, calls, meetings) — account-scoped, so only usable
  // when the opportunity is linked to an account (tender-sourced opps are not).
  const activityAccountId = opp?.accountId || '';
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState<CrmActivityDetail | null>(null);
  const [completeTarget, setCompleteTarget] = useState<CrmActivityDetail | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrmActivityDetail | null>(null);
  const createActivityMut = useCreateActivity(activityAccountId, opportunityId || '');
  const updateActivityMut = useUpdateActivity(activityAccountId, opportunityId || '');
  const completeActivityMut = useCompleteActivity(activityAccountId, opportunityId || '');
  const deleteActivityMut = useDeleteActivity(activityAccountId, opportunityId || '');

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (error || !opp) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8">
        <div className="text-red-500 text-lg font-medium mb-2">Opportunity not found</div>
        <div className="text-gray-500 text-sm mb-4">{(error as Error)?.message || 'Unknown error'}</div>
        <Link to="/crm/pipeline" className="text-primary hover:underline">Back to Pipeline</Link>
      </div>
    );
  }

  const pipelineDef = PIPELINES.find((p) => p.key === opp.pipeline) || PIPELINES[0]!;
  const stageDef = pipelineDef.stages.find((s) => s.key === opp.stage);
  const orgName = opp.companyName || opp.accountName || 'Unknown';

  // Stage navigation — order stages by their `order` field so prev/next are predictable.
  const activeStages = pipelineDef.stages
    .filter((s) => !('isTerminal' in s && (s as { isTerminal?: boolean }).isTerminal))
    .sort((a, b) => ((a as { order?: number }).order || 0) - ((b as { order?: number }).order || 0));
  const wonStage = pipelineDef.stages.find((s) => /won/i.test(s.key));
  const lostStage = pipelineDef.stages.find((s) => /lost/i.test(s.key));
  const isTerminal = !!stageDef && /won|lost/i.test(stageDef.key);

  // ---- Left sidebar content ----
  const leftSidebar = (
    <>
      {/* Metrics */}
      <Card>
        <CardContent className="py-3 space-y-3 text-sm">
          {/* Stage */}
          <div>
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1"><Target className="h-3.5 w-3.5" /> Stage</div>
            {isTerminal ? (
              <Badge style={{ backgroundColor: pipelineDef.color, color: 'white' }}>{stageDef?.label || opp.stage}</Badge>
            ) : (
              <div className="flex items-center gap-2">
                <Select
                  value={opp.stage}
                  disabled={stageMutation.isPending}
                  onValueChange={(v) => {
                    if (v === '__won__') setConfirmWonOpen(true);
                    else if (v === '__lost__') setMarkLostOpen(true);
                    else stageMutation.mutate(v);
                  }}
                >
                  <SelectTrigger className="h-8 text-sm flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {activeStages.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                    {wonStage && <SelectItem value="__won__" className="text-emerald-700">🏆 Mark as Won</SelectItem>}
                    {lostStage && <SelectItem value="__lost__" className="text-red-700">✕ Mark as Lost</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <InlineField
            icon={<DollarSign className="h-3.5 w-3.5" />}
            label="Estimated Value"
            display={opp.estimatedValue ? formatCurrency(opp.estimatedValue, opp.currency) : '—'}
            isEditing={editingField === 'estimatedValue'}
            onEdit={() => { setEditingField('estimatedValue'); setFieldDraft(String(opp.estimatedValue ?? '')); }}
            onCancel={() => setEditingField(null)}
            onSave={() => {
              const n = fieldDraft.trim() === '' ? null : Number(fieldDraft);
              if (n !== null && Number.isNaN(n)) { toast.error('Enter a valid number'); return; }
              updateOppMutation.mutate({ estimatedValue: n });
            }}
            input={(
              <input
                type="number"
                min={0}
                autoFocus
                value={fieldDraft}
                onChange={(e) => setFieldDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingField(null); }}
                className="w-full bg-transparent border-b border-primary outline-none text-base font-semibold"
              />
            )}
          />
          <div>
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-0.5"><Target className="h-3.5 w-3.5" /> Probability</div>
            <p className="text-base font-semibold">{stageDef?.probability ? `${Math.round(stageDef.probability * 100)}%` : opp.probability ? `${opp.probability}%` : '—'}</p>
          </div>
          <InlineField
            icon={<Calendar className="h-3.5 w-3.5" />}
            label="Target Close"
            display={(() => {
              if (!opp.targetDate) return '—';
              const d = opp.targetDate.substring(0, 10);
              const today = new Date().toISOString().substring(0, 10);
              const overdue = !isTerminal && d < today;
              const diffDays = overdue ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : 0;
              return (
                <span className={cn(overdue && 'text-red-600')}>
                  {d}
                  {overdue && <span className="ml-1 text-[10px] font-normal">({diffDays}d overdue)</span>}
                </span>
              );
            })()}
            isEditing={editingField === 'targetDate'}
            onEdit={() => { setEditingField('targetDate'); setFieldDraft(opp.targetDate?.substring(0, 10) ?? ''); }}
            onCancel={() => setEditingField(null)}
            onSave={() => updateOppMutation.mutate({ targetDate: fieldDraft || null })}
            input={(
              <input
                type="date"
                autoFocus
                value={fieldDraft}
                onChange={(e) => setFieldDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditingField(null); }}
                className="w-full bg-transparent border-b border-primary outline-none text-base font-semibold"
              />
            )}
          />
          <div>
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-0.5"><Clock className="h-3.5 w-3.5" /> Created</div>
            <p className="text-base font-semibold">{opp.createdAt ? new Date(opp.createdAt).toLocaleDateString('en-CA') : '—'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Description */}
      {opp.description && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Description</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{opp.description}</p>
          </CardContent>
        </Card>
      )}
    </>
  );

  // ---- Right sidebar content ----
  const rightSidebar = (
    <>
      {/* RFP Actions — Generate Brief */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-purple-600" /> RFP Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Button
            size="sm"
            variant="default"
            className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white w-full"
            disabled={!opp.gdriveFolderId || briefRunning}
            onClick={handleGenerateBrief}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {briefRunning ? 'Generating…' : 'Generate Brief'}
          </Button>
          {!opp.gdriveFolderId && (
            <p className="text-xs text-muted-foreground">Set a Drive folder below first</p>
          )}
          {briefError && (
            <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded p-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /><span>{briefError}</span>
            </div>
          )}
          {briefResult?.briefUrl && (
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-2.5 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-purple-800 flex items-center gap-1.5">
                  <Bot className="h-3.5 w-3.5" /> Brief ready
                </span>
                <a href={briefResult.briefUrl} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="outline" className="h-6 text-xs gap-1 text-purple-700 border-purple-300 hover:bg-purple-100 px-2">
                    <ExternalLink className="h-3 w-3" /> Open
                  </Button>
                </a>
              </div>
              {briefResult.documentsToPrepare.length > 0 && (
                <ul className="text-xs text-purple-800 space-y-0.5">
                  {briefResult.documentsToPrepare.map((doc, i) => (
                    <li key={i} className="flex items-start gap-1"><span className="text-purple-500 shrink-0">·</span><span>{doc}</span></li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* DMS Documents */}
      <AttachmentsList entityType="opportunity" entityId={opportunityId!} />

      {/* Google Drive Folder */}
      <OpportunityDriveFolderSection opportunityId={opportunityId!} />

      {/* Company Info */}
      {opp.companyName && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Building2 className="h-4 w-4" /> Company</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {opp.companyId ? (
              <Link to={`/crm/companies/${opp.companyId}`} className="font-medium hover:underline text-primary">
                {opp.companyName}
              </Link>
            ) : (
              <p className="font-medium">{opp.companyName}</p>
            )}
            {opp.companyIndustry && <div><span className="text-muted-foreground text-xs">Industry</span><p>{opp.companyIndustry}</p></div>}
            {opp.companyCity && <div><span className="text-muted-foreground text-xs">Location</span><p>{opp.companyCity}{opp.companyCountry ? `, ${opp.companyCountry}` : ''}</p></div>}
            {opp.companyWebsite && (
              <a href={opp.companyWebsite} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
                <ExternalLink className="h-3 w-3" /> Website
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stage History */}
      {opp.stageHistory && opp.stageHistory.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Stage History</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {opp.stageHistory.map((h) => (
                <div key={h.id} className="flex flex-wrap items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground">{new Date(h.createdAt).toLocaleDateString('en-CA')}</span>
                  <Badge variant="outline" className="text-xs">{h.fromStage}</Badge>
                  <span className="text-muted-foreground">→</span>
                  <Badge variant="outline" className="text-xs">{h.toStage}</Badge>
                  {h.notes && <span className="text-muted-foreground italic">{h.notes}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );

  // ---- Center tabs content ----
  const centerTabs = (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList>
        {opp.tender && <TabsTrigger value="tender">Tender Source</TabsTrigger>}
        <TabsTrigger value="notes">Notes</TabsTrigger>
        <TabsTrigger value="activities">Activities</TabsTrigger>
        <TabsTrigger value="estimates">Estimates</TabsTrigger>
      </TabsList>

      {/* Tender Source Tab */}
      {opp.tender && (
        <TabsContent value="tender" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Original Tender</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Source:</span> <Badge variant="outline" className="text-xs">{opp.tender.source}</Badge></div>
                {opp.tender.solicitationNumber && <div><span className="text-muted-foreground">Solicitation #:</span> <span className="font-mono text-xs">{opp.tender.solicitationNumber}</span></div>}
                {opp.tender.publishDate && <div><span className="text-muted-foreground">Published:</span> {opp.tender.publishDate}</div>}
                {opp.tender.deadline && <div><span className="text-muted-foreground">Deadline:</span> {opp.tender.deadline}</div>}
                {opp.tender.confidenceScore != null && <div><span className="text-muted-foreground">Confidence:</span> <span className="font-semibold">{opp.tender.confidenceScore}/100</span></div>}
                {opp.tender.relevanceScore != null && <div><span className="text-muted-foreground">AI Relevance:</span> <span className="font-semibold">{opp.tender.relevanceScore}/100</span></div>}
                {opp.tender.matchedKeyword && <div><span className="text-muted-foreground">Keyword:</span> {opp.tender.matchedKeyword}</div>}
                {opp.tender.organisationName && <div><span className="text-muted-foreground">Organisation:</span> {opp.tender.organisationName}</div>}
              </div>
              {opp.tender.summary && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">AI Summary</span>
                  <p className="text-sm italic">{opp.tender.summary}</p>
                </div>
              )}
              {opp.tender.description && (
                <div>
                  <span className="text-xs text-muted-foreground block mb-1">Full Description</span>
                  <p className="text-sm whitespace-pre-wrap max-h-60 overflow-y-auto">{opp.tender.description}</p>
                </div>
              )}
              <Button asChild variant="outline" size="sm" className="gap-1">
                <a href={opp.tender.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3" /> View Original Tender
                </a>
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      )}

      {/* Notes Tab */}
      <TabsContent value="notes" className="mt-4">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><StickyNote className="h-4 w-4" /> Notes</CardTitle>
              <Button size="sm" variant="outline" className="gap-1" disabled={notesMutation.isPending} onClick={() => notesMutation.mutate(notes)}>
                <Save className="h-3 w-3" /> Save
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this opportunity... strategy, contacts, requirements, next steps..."
              rows={14}
              className="font-mono text-sm"
            />
          </CardContent>
        </Card>
      </TabsContent>

      {/* Activities Tab */}
      <TabsContent value="activities" className="mt-4">
        {opp.accountId ? (
          <div className="space-y-6">
            {/* Notes (crm_notes) — primary surface */}
            <CrmNoteFeed entityType="opportunity" entityId={opportunityId!} />

            {/* Activities (crm_activities) — calls, meetings, tasks */}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-700">Activities</h3>
              <ActivityTimeline
                accountId={opp.accountId}
                opportunityId={opportunityId!}
                onAddActivity={() => { setEditingActivity(null); setActivityModalOpen(true); }}
                onEditActivity={(a) => { setEditingActivity(a); setActivityModalOpen(true); }}
                onCompleteActivity={(a) => setCompleteTarget(a)}
                onDeleteActivity={(a) => setDeleteTarget(a)}
              />
            </div>
          </div>
        ) : (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              <p>Notes and activities need a linked account.</p>
              <p className="text-xs mt-1">Link this opportunity to an account to log notes, calls, and meetings.</p>
            </CardContent>
          </Card>
        )}
      </TabsContent>
      <TabsContent value="estimates" className="mt-4">
        <OpportunityEstimatesTab opportunityId={opp.id} />
      </TabsContent>
    </Tabs>
  );

  return (
    <>
      <CrmDetailLayout
        header={
          <div className="space-y-3">
            {/* Title row */}
            <div className="flex items-start gap-3">
              <Link to="/crm/pipeline" className="mt-1 text-muted-foreground hover:text-foreground shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div className="flex-1 min-w-0">
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={titleDraft}
                      maxLength={500}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && titleDraft.trim()) updateOppMutation.mutate({ title: titleDraft.trim() });
                        if (e.key === 'Escape') setIsEditingTitle(false);
                      }}
                      className="flex-1 text-xl font-semibold bg-transparent border-b-2 border-primary outline-none py-0.5"
                    />
                    <Button size="sm" variant="outline" disabled={!titleDraft.trim() || updateOppMutation.isPending} onClick={() => updateOppMutation.mutate({ title: titleDraft.trim() })}>
                      <Check className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setIsEditingTitle(false)}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 group">
                    <h1 className="text-xl font-semibold">{opp.title}</h1>
                    <button
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded text-muted-foreground hover:text-foreground"
                      title="Edit title"
                      onClick={() => { setTitleDraft(opp.title); setIsEditingTitle(true); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <Badge style={{ backgroundColor: pipelineDef.color, color: 'white' }}>{stageDef?.label || opp.stage}</Badge>
                  <Badge variant="outline" className="text-xs">{pipelineDef.label}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{opp.type}</Badge>
                  <Badge variant="outline" className="text-xs capitalize">{opp.priority || 'medium'}</Badge>
                  {opp.accountNumber && <span className="text-xs text-muted-foreground">· {orgName} ({opp.accountNumber})</span>}
                </div>
              </div>
            </div>

          </div>
        }
        left={leftSidebar}
        center={centerTabs}
        right={rightSidebar}
      />

      {/* Modals */}
      <AlertDialog open={confirmWonOpen} onOpenChange={setConfirmWonOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-700">
              <Trophy className="h-5 w-5" /> Mark as Won
            </AlertDialogTitle>
            <AlertDialogDescription>
              Move <span className="font-semibold text-foreground">{opp.title}</span> to the Won stage. This archives the opportunity out of the active pipeline.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700" disabled={stageMutation.isPending} onClick={() => { if (wonStage) stageMutation.mutate(wonStage.key); setConfirmWonOpen(false); }}>
              Yes, mark as Won
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MarkLostModal
        open={markLostOpen}
        onOpenChange={setMarkLostOpen}
        opportunity={opp as unknown as PipelineOpportunity}
        isLoading={markLostMutation.isPending}
        onConfirm={async (reason) => {
          await markLostMutation.mutateAsync({ accountId: opp.accountId, opportunityId: opp.id, reason });
          setMarkLostOpen(false);
        }}
      />

      {/* Activity create / edit / complete / delete */}
      <AddActivityModal
        open={activityModalOpen}
        onOpenChange={(o) => { setActivityModalOpen(o); if (!o) setEditingActivity(null); }}
        activity={editingActivity}
        onSave={async (data) => {
          if (editingActivity) {
            await updateActivityMut.mutateAsync({ activityId: editingActivity.id, input: data });
          } else {
            await createActivityMut.mutateAsync(data);
          }
          setActivityModalOpen(false);
          setEditingActivity(null);
        }}
        isSaving={createActivityMut.isPending || updateActivityMut.isPending}
      />

      <CompleteActivityModal
        open={!!completeTarget}
        onOpenChange={(o) => { if (!o) setCompleteTarget(null); }}
        activity={completeTarget}
        onComplete={async (outcome, notes) => {
          if (!completeTarget) return;
          await completeActivityMut.mutateAsync({ activityId: completeTarget.id, outcome, outcomeNotes: notes });
          setCompleteTarget(null);
        }}
        isLoading={completeActivityMut.isPending}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {deleteTarget?.type === 'note' ? 'note' : 'activity'}?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteTarget) return;
                await deleteActivityMut.mutateAsync(deleteTarget.id);
                setDeleteTarget(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tender Agent side panel */}
      <AgentPanel
        agentId="tender-agent"
        name="Tender Agent"
        tagline="RFP response coordinator"
        intro={`I can help you respond to this RFP. Ask me to generate a brief, list the GDrive files, or guide you through the response process.\n\nOpportunity: **${opp.title}**${opp.tender?.deadline ? `\nDeadline: ${opp.tender.deadline}` : ''}`}
        placeholder="Ask about this tender…"
        variant="purple"
        storagePrefix={`atek-tender-${opportunityId}-`}
        panelMode="side"
      />
    </>
  );
}
